import * as React from 'react';
import { useMemo } from 'react';
import { useModalContainer } from 'ui';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

import StyledAutocompletePopper from './StyledAutocompletePopper';
import StyledPopper from './StyledPopper';
import StyledInput from './StyledInput';
import CodeOption from './CodeOption';
import useCloseOnOutsidePress from './useCloseOnOutsidePress';

const filter = createFilterOptions();
const OPTIONS_LIMIT = 40;

export default function CodeSelector({
  title,
  selectedCodes,
  multiSelect,
  handleSelectionChange,
  options,
  anchorRef,
  // DOM node whose own click handler toggles `open` (CodeMultiSelect's "+"
  // button). Presses on it are left to that handler — dismissing here too
  // would make the same click reopen the popper. Omit it when no single
  // element owns the toggle (CodeNester), so every outside press dismisses.
  toggleRef,
  open,
  setOpen,
  allowNewCodes,
  shouldShowDisabled = false,
  maxListHeight,
  placement
}) {

  const numCodesHidden = options.length - OPTIONS_LIMIT

  // Inside a ui Modal the popper must portal into the dialog's popup, not
  // <body> — see useModalContainer in ui/Modal.jsx.
  const modalContainer = useModalContainer();
  const popperRef = React.useRef(null);

  const handleClose = React.useCallback(() => {
    setOpen(false)
  }, [setOpen]);

  useCloseOnOutsidePress({ open, onClose: handleClose, insideRef: popperRef, toggleRef });

  // The filter text is controlled so that a change of `selectedCodes`
  // identity (the client uuid of a just-created code being swapped for its
  // server id when the response lands) cannot wipe what the user is typing:
  // MUI resets the input from its value-change effect with a null event, while
  // real selections and clears arrive with an event, and those still clear it.
  const [inputValue, setInputValue] = React.useState('');
  React.useEffect(() => {
    if (!open) setInputValue('');
  }, [open]);
  const handleInputChange = (event, value, reason) => {
    if (reason === 'reset' && event === null) return;
    setInputValue(value);
  };

  // filter options for unique names
  const filterUniqueByName = options => {
    const seen = new Set();
    return options.filter(option => {
      const trimmedName = option?.name?.trim();
      if (seen.has(trimmedName)) return false;
      seen.add(trimmedName);
      return true;
    });
  };

  const uniqueOptions = useMemo(() => open ? filterUniqueByName(options) : [], [open, options]);

  const id = open ? 'code-searcher' : undefined;
  if (!anchorRef) return <></>

  const filterOptions = (options, params) => {
    const filtered = filter(options, params).slice(0, OPTIONS_LIMIT); 

    const { inputValue } = params;
    // Suggest the creation of a new value
    const newCode = {
      inputValue,
      name: `Add Code: "${inputValue}"`,
    }

    const isExactMatch = filtered.some((option) => option.name === inputValue);
    const isEmpty = inputValue.trim() === ''

    if (allowNewCodes && !isExactMatch && !isEmpty) {
      return [newCode, ...filtered];
    }

    return filtered;
  }

  return (
    <>
      <StyledPopper ref={popperRef} id={id} anchorEl={anchorRef} open={open} placement={placement} container={modalContainer}>
        <div style={{ padding: '10px' }}>
          <Box
            sx={(t) => ({
              borderBottom: 'none',
              padding: '8px 10px',
              color: '#343A40',
              lineHeight: '100%',
              fontSize: '18px',
              fontWeight: 500,
              ...t.applyStyles('light', {
                borderBottom: 'none',
              }),
            })}
          >
            {title}
          </Box>
          <Autocomplete
            open
            autoHighlight
            freeSolo={allowNewCodes}
            clearOnBlur={true}
            clearOnEscape={false}
            multiple={multiSelect}
            onClose={(event, reason) => {
              if (reason === 'escape') handleClose();
            }}
            value={selectedCodes}
            onChange={(event, value) => handleSelectionChange(value)}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            disableCloseOnSelect
            renderValue={() => null}
            noOptionsText="No codes"
            renderOption={(props, option, { selected }) => (
              <CodeOption
                key={props.key}
                option={option}
                selected={selected}
                shouldShowSelected={multiSelect}
                shouldShowDisabled={shouldShowDisabled}
                {...props}
              />
            )}
            options={uniqueOptions}
            filterOptions={filterOptions}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <StyledInput
                ref={params.slotProps.input.ref}
                inputProps={params.slotProps.htmlInput}
                autoFocus
                placeholder="Filter codes"
              />
            )}
            slots={{
              popper: StyledAutocompletePopper
            }}
            slotProps={{
              popper: { maxListHeight: maxListHeight || 300 }
            }}
          />
        </div>
        { numCodesHidden > 0 &&
        <>
          <Divider />
          <Box sx={{ textAlign: 'center', padding: '1em', color: 'rgb(151, 151, 151)' }}>
            {`${numCodesHidden} codes not shown. Use search to find them.`}
          </Box>
        </>
        }
      </StyledPopper>
    </>
  );
}